import {
  FUNNEL_MARKETING_FALLBACK,
  FUNNEL_QUESTION_DEFAULTS,
  HELPER_BUYER_CAPITAL_COMPANY,
  HELPER_BUYER_COMPANY_ABOUT,
  LABEL_BUYER_CAPITAL_COMPANY,
  LABEL_BUYER_COMPANY_ABOUT,
  LABEL_BUYER_COMPANY_NAME,
  LABEL_BUYER_COMPANY_TITLE,
  LABEL_BUYER_COMPANY_WEBSITE,
  LABEL_BUYER_OPERATOR_NAME,
  LABEL_BUYER_OPERATOR_NAME_OPTIONAL,
  OPTION_BUYER_EQUITY_SOURCE_COMMITTED,
  OPTION_BUYER_EQUITY_SOURCE_ON_HAND,
  OPTION_BUYER_EQUITY_SOURCE_RAISING,
  OPTION_BUYER_OPERATOR_EXISTING_MANAGEMENT,
  OPTION_BUYER_OPERATOR_GENERAL_MANAGER,
  OPTION_BUYER_OPERATOR_PRINCIPAL,
  OPTION_BUYER_TYPE_COMPANY,
  OPTION_BUYER_TYPE_FUND,
  OPTION_BUYER_TYPE_INDIVIDUAL,
  QUESTION_BUYER_EQUITY_SOURCE,
  QUESTION_BUYER_TYPE,
  type FunnelQuestionKey,
} from "@/portal/pages/prequalify/prequalContent";

/**
 * The funnel question blocks the call never shows: it asks no operating
 * geography, and its contact fields carry no helper text. Every OTHER
 * `FUNNEL_QUESTION_DEFAULTS` key is a call block.
 */
const NOT_ON_THE_CALL = [
  "question-buyer-operating-geography",
  "option-buyer-geography-near",
  "option-buyer-geography-region",
  "option-buyer-geography-anywhere",
  "helper-buyer-email",
  "helper-buyer-phone",
  "helper-buyer-phone-given",
] as const satisfies readonly FunnelQuestionKey[];

type CallQuestionBlock = Exclude<FunnelQuestionKey, (typeof NOT_ON_THE_CALL)[number]>;

const CALL_QUESTION_DEFAULTS = Object.fromEntries(
  Object.entries(FUNNEL_QUESTION_DEFAULTS).filter(
    ([key]) => !(NOT_ON_THE_CALL as readonly string[]).includes(key),
  ),
) as Record<CallQuestionBlock, string>;

/**
 * The call questions whose wording is a Document Language `funnel-copy` block
 * (M1), keyed by BLOCK KEY, each with its compiled-in default — the funnel's own
 * default, imported from `prequalContent.ts`, never retyped: the company blocks
 * from the marketing fallback, every other question from
 * `FUNNEL_QUESTION_DEFAULTS`.
 *
 * <p>The server sends the PUBLISHED text of exactly these blocks on the call
 * state (`BuyerPrequalCallStateDto.funnelCopy`, listed server-side by
 * `BuyerCallFunnelCopyBlocks.All`), so a Document Language publish reaches the
 * call as it reaches the funnel. The default renders only when the state
 * carries no text for a block. `callFunnelCopy.test.ts` pins this key set to the
 * server's list and every default to the funnel's.</p>
 */
export const CALL_FUNNEL_COPY_DEFAULTS = {
  "question-buyer-type": QUESTION_BUYER_TYPE,
  "option-buyer-type-individual": OPTION_BUYER_TYPE_INDIVIDUAL,
  "option-buyer-type-company": OPTION_BUYER_TYPE_COMPANY,
  "option-buyer-type-fund": OPTION_BUYER_TYPE_FUND,
  "label-buyer-capital-company": LABEL_BUYER_CAPITAL_COMPANY,
  "helper-buyer-capital-company": HELPER_BUYER_CAPITAL_COMPANY,
  "question-buyer-equity-source": QUESTION_BUYER_EQUITY_SOURCE,
  "option-buyer-equity-source-on-hand": OPTION_BUYER_EQUITY_SOURCE_ON_HAND,
  "option-buyer-equity-source-committed": OPTION_BUYER_EQUITY_SOURCE_COMMITTED,
  "option-buyer-equity-source-raising": OPTION_BUYER_EQUITY_SOURCE_RAISING,
  "label-buyer-company-name": LABEL_BUYER_COMPANY_NAME,
  "label-buyer-company-title": LABEL_BUYER_COMPANY_TITLE,
  "label-buyer-company-website": LABEL_BUYER_COMPANY_WEBSITE,
  "label-buyer-company-about": LABEL_BUYER_COMPANY_ABOUT,
  "helper-buyer-company-about": HELPER_BUYER_COMPANY_ABOUT,
  "option-buyer-operator-principal": OPTION_BUYER_OPERATOR_PRINCIPAL,
  "option-buyer-operator-general-manager": OPTION_BUYER_OPERATOR_GENERAL_MANAGER,
  "option-buyer-operator-existing-management": OPTION_BUYER_OPERATOR_EXISTING_MANAGEMENT,
  "label-buyer-operator-name": LABEL_BUYER_OPERATOR_NAME,
  "label-buyer-operator-name-optional": LABEL_BUYER_OPERATOR_NAME_OPTIONAL,
  "step-title-buyer-goals": FUNNEL_MARKETING_FALLBACK.stepTitleBuyerGoals,
  ...CALL_QUESTION_DEFAULTS,
} as const satisfies Record<string, string>;

export type CallFunnelCopyBlock = keyof typeof CALL_FUNNEL_COPY_DEFAULTS;

/** The published funnel copy the call state carries, by block key. */
export type CallFunnelCopy = Readonly<Record<string, string>> | null | undefined;

/** One block's wording on the call: the published text when the state carries
 *  a non-blank one, else the compiled default. A blank never renders — `??`
 *  would let `""` through, so it is trimmed and tested. */
export function callCopy(funnelCopy: CallFunnelCopy, block: CallFunnelCopyBlock): string {
  const published = funnelCopy?.[block];
  return published && published.trim() ? published : CALL_FUNNEL_COPY_DEFAULTS[block];
}
